/**
 * AI Settings Storage Utility
 * Manages AI settings persistence using browser session storage
 */

export interface AISettingsData {
  title_length: number;
  description_length: number;
  keywords_count: number;
  custom_instruction: string;
}

// Default settings as per documentation
export const DEFAULT_AI_SETTINGS: AISettingsData = {
  title_length: 80,        // Default: 80 characters (Range: 30-150)
  description_length: 200, // Default: 200 characters (Range: 50-300)
  keywords_count: 50,      // Default: 50 keywords (Range: 10-100)
  custom_instruction: ''   // Default: empty string
};

// Settings ranges for validation
export const AI_SETTINGS_RANGES = {
  title_length: { min: 30, max: 150 },
  description_length: { min: 50, max: 300 },
  keywords_count: { min: 10, max: 100 }
} as const;

// Session storage key
const STORAGE_KEY = 'ai_settings';

/**
 * Load AI settings from session storage
 * Returns default settings if none found or invalid
 */
export const loadAISettings = (): AISettingsData => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return { ...DEFAULT_AI_SETTINGS };
    }

    const parsed = JSON.parse(stored) as AISettingsData;
    
    // Validate and sanitize loaded settings
    return {
      title_length: validateRange(parsed.title_length, AI_SETTINGS_RANGES.title_length, DEFAULT_AI_SETTINGS.title_length),
      description_length: validateRange(parsed.description_length, AI_SETTINGS_RANGES.description_length, DEFAULT_AI_SETTINGS.description_length),
      keywords_count: validateRange(parsed.keywords_count, AI_SETTINGS_RANGES.keywords_count, DEFAULT_AI_SETTINGS.keywords_count),
      custom_instruction: typeof parsed.custom_instruction === 'string' ? parsed.custom_instruction : DEFAULT_AI_SETTINGS.custom_instruction
    };
  } catch (error) {
    console.warn('Failed to load AI settings from session storage:', error);
    return { ...DEFAULT_AI_SETTINGS };
  }
};

/**
 * Save AI settings to session storage
 */
export const saveAISettings = (settings: AISettingsData): void => {
  try {
    // Validate settings before saving
    const validatedSettings: AISettingsData = {
      title_length: validateRange(settings.title_length, AI_SETTINGS_RANGES.title_length, DEFAULT_AI_SETTINGS.title_length),
      description_length: validateRange(settings.description_length, AI_SETTINGS_RANGES.description_length, DEFAULT_AI_SETTINGS.description_length),
      keywords_count: validateRange(settings.keywords_count, AI_SETTINGS_RANGES.keywords_count, DEFAULT_AI_SETTINGS.keywords_count),
      custom_instruction: typeof settings.custom_instruction === 'string' ? settings.custom_instruction : DEFAULT_AI_SETTINGS.custom_instruction
    };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(validatedSettings));
  } catch (error) {
    console.error('Failed to save AI settings to session storage:', error);
  }
};

/**
 * Reset AI settings to defaults and clear session storage
 * Returns the default settings
 */
export const resetAISettings = (): AISettingsData => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear AI settings from session storage:', error);
  }
  return { ...DEFAULT_AI_SETTINGS };
};

/**
 * Check if current settings are default settings
 */
export const isDefaultSettings = (settings: AISettingsData): boolean => {
  return (
    settings.title_length === DEFAULT_AI_SETTINGS.title_length &&
    settings.description_length === DEFAULT_AI_SETTINGS.description_length &&
    settings.keywords_count === DEFAULT_AI_SETTINGS.keywords_count &&
    settings.custom_instruction === DEFAULT_AI_SETTINGS.custom_instruction
  );
};

/**
 * Validate a numeric value against a range
 */
const validateRange = (value: number, range: { min: number; max: number }, defaultValue: number): number => {
  if (typeof value !== 'number' || isNaN(value)) {
    return defaultValue;
  }
  return Math.min(Math.max(value, range.min), range.max);
};

/**
 * Get settings formatted for database storage
 * Returns the settings object without custom_instruction (stored separately)
 */
export const getSettingsForDatabase = (settings: AISettingsData) => {
  return {
    title_length: settings.title_length,
    description_length: settings.description_length,
    keywords_count: settings.keywords_count
  };
};